<template>
  <div class="mb-16">
    <CheckoutHeader :current-step="1" />
    <CheckoutLayout
      :next-link="nextLink"
      :title="$t('checkout.addons')"
      :has-discount-code="false"
      :has-gift-card="false"
    >
      <template #header >
        <CheckoutTicketHeader />
      </template>

      <div class="space-y-4">
        <CheckoutUpsaleTicket
           v-for="addon in addonAvailableToBasket"
           :key="addon.id"
           :ticket="addon"
           :eventID="eventID"
           :event="checkoutStore.event"
        />
      </div>
    </CheckoutLayout>
  </div>
</template>

<script setup lang="ts">
  import { useHead } from '@unhead/vue';
  import { computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { onBeforeRouteLeave } from 'vue-router';

  import CheckoutHeader from '@/components/checkout/CheckoutHeader.vue';
  import CheckoutLayout from '@/components/checkout/CheckoutLayout.vue';
  import CheckoutTicketHeader from '@/components/checkout/CheckoutTicketHeader.vue';
  import CheckoutUpsaleTicket from '@/components/checkout/CheckoutUpsaleTicket.vue';
  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { useToastStore } from '@/stores/toastStore';
  import type { TicketCheckoutData } from '@/types';
  import { useCheckoutNavigation } from '@/utils/checkoutNavigation';

  const { t } = useI18n();
  const checkoutStore = useCheckoutStore();
  const eventID = computed(() => checkoutStore.getCheckoutID());
  const cart = useCartStore();

  onBeforeRouteLeave(() => {
    cart.cleanUpEmptyEvents(true);
  });
  const toastStore = useToastStore();
  const { getNextRoute } = useCheckoutNavigation();

  const nextLink = computed(() => getNextRoute('checkout-upsale') ?? '/checkout/information');

  //Watch cart.differentCount and if it changes to true, show a toast informing the user they could not reserve the specified amount
  watch(() => cart.differentCount, newValue => {
    if (newValue) {
      const message = cart.differentCountMessage.message;
      const count = cart.differentCountMessage.count;
      const ticketsText = (count == 1 ? t('common.ticket') : t('common.ticket_plural')).toLocaleLowerCase();
      console.log('Cart differentCount is true, showing toast with message:', message, 'and count:', count);
      if(message && message == 'initial'){
        toastStore.showToast(t('checkout.reservation_mismatch', {tickets: ticketsText}), 15000);
      }
      else if(message && count){
        toastStore.showToast(t('checkout.reservation_mismatch_' + message, { count, tickets: ticketsText }), 15000);
      }
      cart.differentCount = false; // reset the flag
    }
  });
  
  // An Array of ticket ids that are upsale (addons), and are allowed to be added to the basket.
  // getAvailableUpsaleTicketsForBasket is called inside the computed so Vue tracks cart.items reactively.
  const addonAvailableToBasket = computed<TicketCheckoutData[]>(() => {
    const upsaleTicketsAvailableToBasket = cart.getAvailableUpsaleTicketsForBasket();
      
    return checkoutStore.upsaleTickets.filter((upsale_ticket) => {
      if (!upsale_ticket.upsale_flag) return false;
      
      return upsaleTicketsAvailableToBasket.includes(Number(upsale_ticket.id));
    });
  });

  useHead({
    title: t('checkout.addons'),
    meta: [
      {
        property: 'og:title',
        content: t('checkout.addons'),
      },
    ],
  });
</script>
